"use client";

import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { ShopRealtimeMessage } from "@jurni/schemas";
import { subscribeToShopEventStream } from "@/services/realtime";

const RECONNECT_DELAY_MS = 3000;

type ShopEventListener = (message: ShopRealtimeMessage) => void;

interface ShopEventsContextValue {
  subscribeReconnect: (listener: () => void) => () => void;
  subscribe: (listener: ShopEventListener) => () => void;
}

const ShopEventsContext = createContext<ShopEventsContextValue | undefined>(undefined);

export const useShopEventsContext = () => {
  const context = useContext(ShopEventsContext);
  if (!context) {
    throw new Error("useShopEventsContext must be used within a ShopEventsProvider");
  }
  return context;
};

export const ShopEventsProvider = ({ shopId, children }: { shopId: number; children: React.ReactNode }) => {
  const reconnectListenersRef = useRef(new Set<() => void>());
  const listenersRef = useRef<Set<ShopEventListener>>(new Set());

  useEffect(() => {
    const abortController = new AbortController();
    let stopped = false;

    const run = async () => {
      while (!stopped) {
        const stream = await subscribeToShopEventStream(abortController.signal);
        if (stream) {
          reconnectListenersRef.current.forEach((listener) => {
            try { listener(); } catch (error) { console.error("Shop reconnect listener failed", error); }
          });
          try {
            for await (const message of stream) {
              if (stopped) return;
              listenersRef.current.forEach((listener) => {
                try {
                  listener(message);
                } catch (error) {
                  console.error("Shop event listener failed", error);
                }
              });
            }
          } catch (error) {
            if (stopped || (error instanceof Error && error.name === "AbortError")) return;
          }
        }
        if (stopped) return;
        await new Promise((resolve) => setTimeout(resolve, RECONNECT_DELAY_MS));
      }
    };

    run();

    return () => {
      stopped = true;
      abortController.abort();
    };
  }, [shopId]);

  const value = useMemo<ShopEventsContextValue>(
    () => ({
      subscribeReconnect: (listener) => {
        reconnectListenersRef.current.add(listener);
        return () => { reconnectListenersRef.current.delete(listener); };
      },
      subscribe: (listener) => {
        listenersRef.current.add(listener);
        return () => {
          listenersRef.current.delete(listener);
        };
      },
    }),
    [],
  );

  return <ShopEventsContext.Provider value={value}>{children}</ShopEventsContext.Provider>;
};
